import ContentLoader from 'react-content-loader';
import { withErrorBoundary } from 'react-error-boundary';
import styled, { css } from 'styled-components';
import {
    BorderRadiusMedium,
    ColorNeutralGrey1,
    ColorNeutralGrey5,
    FontSize14,
    FontSize18,
    Spacing16
} from 'autogen/design-tokens/tokens';
import Card from 'components/generic/card';
import useSegmentation from 'hooks/user/useSegmentation';
import type { ContentCardSkeletonProps } from './types';

const StyledCard = styled(Card)<{ $isTransparent: boolean }>`
    ${({ $isTransparent }) =>
        !$isTransparent &&
        css`
            background-color: ${({ theme }) => theme.ColorBackgroundPrimary};
        `}
`;

function ContentCardSkeleton({ isTransparent = false, aspectRatio = '4/3' }: ContentCardSkeletonProps) {
    const { tenant } = useSegmentation();
    const borderRadius = tenant === 'Matas' ? BorderRadiusMedium : 0;
    const contentPadding = isTransparent ? 0 : Spacing16;
    const svgWidth = 255;
    const splitAspectRatio = aspectRatio.includes('/')
        ? {
              width: Number(aspectRatio.split('/')[0]),
              height: Number(aspectRatio.split('/')[1])
          }
        : { width: 4, height: 3 };
    const imageHeight = (svgWidth * splitAspectRatio.height) / splitAspectRatio.width;

    return (
        <StyledCard $isTransparent={isTransparent}>
            <ContentLoader
                speed={2}
                preserveAspectRatio="none"
                viewBox={`0 0 ${svgWidth} ${imageHeight + 94}`}
                backgroundColor={ColorNeutralGrey5}
                foregroundColor={ColorNeutralGrey1}
            >
                {/* Skeleton for image */}
                <rect
                    x="0"
                    y="0"
                    rx={borderRadius}
                    ry={isTransparent ? borderRadius : 0}
                    width="100%"
                    height={imageHeight}
                />
                {/* Skeleton for subheading */}
                <rect
                    x={contentPadding}
                    y={imageHeight + 16}
                    rx={borderRadius}
                    ry={borderRadius}
                    width="100"
                    height={FontSize14}
                />
                {/* Skeleton for heading line 1 */}
                <rect
                    x={contentPadding}
                    y={imageHeight + 38}
                    rx={borderRadius}
                    ry={borderRadius}
                    width="220"
                    height={FontSize18}
                />
                {/* Skeleton for heading line 2 */}
                <rect
                    x={contentPadding}
                    y={imageHeight + 60}
                    rx={borderRadius}
                    ry={borderRadius}
                    width="180"
                    height={FontSize18}
                />
            </ContentLoader>
        </StyledCard>
    );
}

export default withErrorBoundary(ContentCardSkeleton, {
    fallback: null,
    onError(error, info) {
        console.error(error, info);
    }
});
